// ApprovalRow — one prepared action awaiting the human's decision.
// The seam between what ARIA prepares and what the user approves.
// Presentational only: all data comes from computeApprovalQueue() items,
// all behavior from the handlers passed in. No mock values.
import React from 'react';
import IntelligenceTrace from './IntelligenceTrace';

const ICONS = {
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 7L12 14L22 7" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
    </svg>
  ),
  doc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M6 3h8l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v5h5" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <circle cx="12" cy="12" r="9" />
      <polyline points="8 12 11 15 16 9" />
    </svg>
  ),
};

const URGENCY_LABEL = { high: 'Urgent', med: 'Today', low: 'This week' };

// Derive the causal trace from the item's real provenance fields —
// source (where), sourceDetail (the detected signal), title (the action).
function traceFor(item) {
  const nodes = [];
  if (item.source) nodes.push({ type: 'source', label: item.source });
  if (item.sourceDetail) {
    nodes.push({ type: 'signal', label: item.sourceDetail.replace(/^Triggered by\s*/i, '') });
  }
  if (item.title) nodes.push({ type: 'action', label: item.title });
  return nodes.length >= 2 ? nodes : [];
}

export default function ApprovalRow({
  item,
  draftText,
  isExecuting,
  isAuthed,
  onApprove,
  onEdit,
  onSnooze,
  onDismiss,
  onSend,
}) {
  const isDraftedByAria = item.type === 'email_reply' && !!draftText;
  const isDrafting = item.type === 'email_reply' && !draftText && isAuthed;
  const canSend = item.type === 'email_reply' && isAuthed && !!onSend && !!item.meta?.email;

  // Two-step send: first click arms the button, second click sends.
  // Auto-disarms after 5s so an armed button never lingers.
  const [confirmingSend, setConfirmingSend] = React.useState(false);
  React.useEffect(() => {
    if (!confirmingSend) return undefined;
    const t = setTimeout(() => setConfirmingSend(false), 5000);
    return () => clearTimeout(t);
  }, [confirmingSend]);

  return (
    <div className={`hm-approval-row urgency-${item.urgency} ${isExecuting ? 'executing' : ''}`}>
      <div className="hm-approval-main">
        <div className={`hm-approval-icon icon-${item.type}`} aria-hidden="true">
          {ICONS[item.icon] || ICONS.check}
        </div>
        <div className="hm-approval-body">
          <div className="hm-approval-header">
            <span className="hm-approval-title">{item.title}</span>
            <span className={`hm-tag hm-tag-urgency-${item.urgency}`}>
              {URGENCY_LABEL[item.urgency] || 'This week'}
            </span>
            <span className="hm-tag hm-tag-source">{item.source}</span>
          </div>
          {item.context && <div className="hm-approval-context">{item.context}</div>}

          <div className="hm-approval-preview">
            <div className="hm-approval-preview-label">
              {isDraftedByAria ? 'Draft · written by ARIA in your voice' : 'Preview'}
              {isDrafting && <span className="hm-approval-drafting"> · drafting…</span>}
            </div>
            <div className="hm-approval-preview-body">{draftText || item.preview}</div>
          </div>

          <IntelligenceTrace nodes={traceFor(item)} />
        </div>
      </div>

      <div className="hm-approval-actions" role="group" aria-label={`Actions for: ${item.title}`}>
        <button
          type="button"
          className="hm-btn hm-btn-approve"
          onClick={() => onApprove(item)}
          disabled={isExecuting}
          title={item.type === 'email_reply' ? 'Approve — saves the reply to Gmail Drafts for you to send' : 'Approve and execute'}
        >
          {isExecuting ? 'Working…' : item.type === 'email_reply' ? 'Approve & draft' : 'Approve'}
        </button>
        {canSend && (
          <button
            type="button"
            className={`hm-btn ${confirmingSend ? 'hm-btn-send-confirm' : 'hm-btn-quiet'}`}
            onClick={() => {
              if (!confirmingSend) { setConfirmingSend(true); return; }
              setConfirmingSend(false);
              onSend(item);
            }}
            disabled={isExecuting}
            title={confirmingSend ? `Click again to send to ${item.meta.email}` : 'Send the reply immediately (asks once to confirm)'}
          >
            {confirmingSend ? `Confirm send to ${item.meta.email}` : 'Send now'}
          </button>
        )}
        <button
          type="button"
          className="hm-btn hm-btn-quiet"
          onClick={() => onEdit(item)}
          disabled={isExecuting}
          title="Refine with ARIA before approving"
        >
          Edit
        </button>
        <button
          type="button"
          className="hm-btn hm-btn-quiet"
          onClick={() => onSnooze(item, 4)}
          disabled={isExecuting}
          title="Snooze for 4 hours"
        >
          Snooze
        </button>
        <button
          type="button"
          className="hm-btn hm-btn-quiet hm-btn-dismiss"
          onClick={() => onDismiss(item)}
          disabled={isExecuting}
          title="Dismiss — ARIA learns not to suggest this again"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
